import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), ' +
  'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Focus the first control inside `root`. For a radio group the checked option is preferred,
 * so the keyboard lands on the current choice rather than the first one.
 */
export function focusFirst(root: HTMLElement | null): boolean {
  if (!root) return false;
  const first = root.querySelector<HTMLElement>(FOCUSABLE);
  if (!first) return false;
  let target = first;
  if (first instanceof HTMLInputElement && first.type === 'radio' && !first.checked && first.name) {
    const checked = root.querySelector<HTMLInputElement>(
      `input[type="radio"][name="${CSS.escape(first.name)}"]:checked`,
    );
    if (checked) target = checked;
  }
  target.focus();
  return document.activeElement === target;
}

/** Focus the first control marked invalid inside `root`, so an error is heard as well as seen. */
export function focusFirstInvalid(root: HTMLElement | null): boolean {
  if (!root) return false;
  const invalid = root.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!invalid) return false;
  invalid.focus();
  return document.activeElement === invalid;
}

/** After a validation pass sets errors, move focus to the first invalid control in the form. */
export function useFocusFirstInvalid(
  form: RefObject<HTMLElement | null>,
  errors: Record<string, string>,
): void {
  useEffect(() => {
    if (Object.keys(errors).length === 0) return;
    const id = window.requestAnimationFrame(() => focusFirstInvalid(form.current));
    return () => window.cancelAnimationFrame(id);
  }, [form, errors]);
}
